import { DateTime } from '@ksyos/date-time';
import { Text } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { contactFileExtensions } from '@common/utils/fileExtensions';
import type { ICustomControlRequestAddEditProps } from '../../../interfaces/';
import { getServiceLabel } from '../../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../../common/enums/filesUrlBase';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { RequestStatus } from '../../../../../../../common/enums/requestStatus';
import { ToastType } from '../../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../../translate';
import { canCreateOsasvc } from '../../../../../../utils/canCreateOsasvc';
import { showServiceToast } from '../../../../../../utils/showToast';
import { CancelButton } from '../../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../../elements/carePathButtons/SaveSendButton';
import { Button, ButtonContainer, Form } from '../../../../../elements/common';
import { FormContextProvider } from '../../../../../elements/formContext';
import { Modal } from '../../../../../elements/modals/Modal';
import { DateField } from '../../../../../fields/Date';
import { TextAreaField } from '../../../../../fields/TextArea';
import { AssetDropzoneWithActions } from '../../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../../utils/v1/CareProviderSelector';
import { NewOrEditRequestProcessContainer } from '../../../utils/v1/NewOrEditRequestProcessContainer';

export function OsasvcV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const navigate = useNavigate();
    const [buttonDisabled, setButtonDisabled] = useState<boolean | undefined>();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalMessage, setModalMessage] = useState<string>();
    const [canCreateRequestOsasvc, setCanCreateRequestOsasvc] = useState<boolean>(false);

    useEffect(() => {
        checkForCompletedRequest();
    }, []);

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                    hideCorrectGpWarning={true}
                />

                <FormContextProvider
                    value={{
                        placeholderGroupName: 'procedure',
                        item: props.request.customControlData,
                        errors: props.errors,
                        customControlData: true,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <DateField
                        error={props.errors.get('customControlData.dateContact')}
                        fieldKey="dateContact"
                        hasErrors={props.errors.has('customControlData.dateContact')}
                        isRequired
                        label={translateKey('request.dateContact')}
                        maxDate={DateTime.now().format()}
                        placeholder={`${translateKey('common.date')}...`}
                        size="medium"
                    />

                    <TextAreaField
                        error={props.errors.get('customControlData.anamnese')}
                        fieldKey="anamnese"
                        label={translateKey('carePlan.anamnese')}
                        placeholder="Vul hier de anamnese in..."
                    />

                    <TextAreaField
                        error={props.errors.get('customControlData.onderzoek')}
                        fieldKey="onderzoek"
                        label={translateKey('procedure.procedure')}
                        placeholder="Vul hier alle onderzoeksresultaten in..."
                    />

                    <TextAreaField
                        error={props.errors.get('customControlData.diagnose')}
                        fieldKey="diagnose"
                        label={translateKey('diagnosis.diagnosis')}
                        placeholder="Vul hier je werkdiagnose en mogelijke differentiaaldiagnoses in..."
                    />

                    <TextAreaField
                        error={props.errors.get('customControlData.beleid')}
                        fieldKey="beleid"
                        label={translateKey('diagnosticReport.policy')}
                        placeholder="Vul hier het beleid in..."
                    />

                    <AssetDropzoneWithActions
                        acceptedFiles={contactFileExtensions}
                        assets={props.request.assets}
                        assetsBaseUrl={FilesUrlBase.requests}
                        cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                        onChange={async (assets) => await props.handleChange('assets', assets)}
                        onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                        processActivityId={props.request.id}
                        service={props.service}
                        showAdditionalFilesCheckbox={props.previousRequest !== undefined}
                        updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                    />
                </FormContextProvider>

                <Modal
                    actionButtons={[
                        <Button
                            color="secondary"
                            onClick={() => {
                                setShowModal(false);
                                navigate(-1);
                            }}
                        >
                            {'Ok'}
                        </Button>,
                    ]}
                    automation="cannotContinueModal"
                    isOpen={showModal}
                    onClose={() => {
                        setShowModal(false);
                        navigate(-1);
                    }}
                    size={'small'}
                    title={'Vervolgconsult kan niet worden gestart'}
                >
                    <Text>{modalMessage}</Text>
                </Modal>

                <ButtonContainer
                    alignRight
                    buttons={getButtons()}
                />
            </Form>
        </NewOrEditRequestProcessContainer>
    );

    async function checkForCompletedRequest() {
        setButtonDisabled(true);

        if (props.process?.status === RequestStatus.draft) {
            setButtonDisabled(false);
            setCanCreateRequestOsasvc(true);
        } else {
            const { isOsasoCompleted, isOsasc2Completed, isOsasvoCompleted } = await canCreateOsasvc(
                props.request.patientId
            );
            const canCreateConsult = (isOsasoCompleted && isOsasc2Completed) || isOsasvoCompleted;
            setShowModal(!canCreateConsult);
            setButtonDisabled(!canCreateConsult);
            setCanCreateRequestOsasvc(canCreateConsult);
            setModalMessage(
                !isOsasoCompleted
                    ? translateKey('osasPermission.cantCreateOsasvcOsasoMissing')
                    : translateKey('osasPermission.cantCreateOsasvcOsasc2Missing')
            );
        }
    }

    async function validateAndSaveRequest(saveType: 'final' | 'draft') {
        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);
        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        await props.onSave(requestId);
    }

    function getButtons() {
        const buttons = [
            <CancelButton
                cancel={props.onCancel}
                entity={ProcessEntity.request}
                serviceCode={props.service.code}
                serviceType={props.service.serviceType}
            />,
        ];

        if (canCreateRequestOsasvc) {
            buttons.push(
                <SaveButton
                    buttonDisabled={buttonDisabled}
                    save={async () => {
                        await validateAndSaveRequest('draft');
                    }}
                />,
                <SaveSendButton
                    buttonDisabled={buttonDisabled}
                    saveSend={async () => {
                        await validateAndSaveRequest('final');
                    }}
                />
            );
        }

        return buttons;
    }
}

export function osasvcV1RequestInitializeCustomControlData() {
    return {
        dateContact: null,
        anamnese: null,
        onderzoek: null,
        diagnose: null,
        beleid: null,
    };
}
